// pages/setup/components/LazyLoadEditor.tsx
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from './Switch';
import { Info } from 'lucide-react';

interface LazyLoadEditorProps {
    config: {
        enabled: boolean;
        threshold: number;
        color: string;
    };
    onUpdate: (field: string, value: any) => void;
}

export function LazyLoadEditor({ config, onUpdate }: LazyLoadEditorProps) {
    const renderInfoTooltip = (text: string) => (
        <div className="group relative inline-block ml-2">
            <Info className="h-4 w-4 text-gray-400 cursor-help" />
            <div className="invisible group-hover:visible absolute z-10 w-64 p-2 mt-1 text-xs text-white bg-gray-900 rounded-lg shadow-lg -left-24">
                {text}
            </div>
        </div>
    );

    return (
        <div className="space-y-6">
            {/* Enable/Disable Toggle */}
            <div className="flex items-center justify-between p-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg border border-gray-200">
                <div>
                    <Label className="text-base font-medium">Ativar Lazy Load</Label>
                    <p className="text-sm text-gray-500 mt-1">
                        Carregamento otimizado da página
                    </p>
                </div>
                <Switch
                    checked={config.enabled}
                    onCheckedChange={(checked) => onUpdate('enabled', checked)}
                />
            </div>

            {config.enabled && (
                <div className="space-y-4">
                    <div className="space-y-2">
                        <Label className="flex items-center">
                            Tempo Limite (ms)
                            {renderInfoTooltip(
                                'Tempo máximo de espera até o bloco carregar antes de exibir a página'
                            )}
                        </Label>
                        <Input
                            type="number"
                            min="1000"
                            step="100"
                            value={config.threshold}
                            onChange={(e) => onUpdate('threshold', parseInt(e.target.value))}
                        />
                        <p className="text-xs text-gray-500">
                            Recomendado: entre 3000ms e 5000ms
                        </p>
                    </div>

                    <div className="space-y-2">
                        <Label className="flex items-center">
                            Cor do Loader
                            {renderInfoTooltip('Cor do indicador de carregamento')}
                        </Label>
                        <div className="flex gap-2">
                            <Input
                                type="color"
                                value={config.color}
                                onChange={(e) => onUpdate('color', e.target.value)}
                                className="w-20 h-10 cursor-pointer"
                            />
                            <Input
                                value={config.color}
                                onChange={(e) => onUpdate('color', e.target.value)}
                                placeholder="#3498db"
                                className="flex-1"
                            />
                        </div>
                    </div>

                    {/* Preview */}
                    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <Label className="text-sm font-medium mb-2 block">Preview do Loader</Label>
                        <div className="flex items-center justify-center p-6 bg-white rounded border border-gray-200">
                            <div className="relative">
                                <div
                                    className="animate-spin rounded-full h-12 w-12 border-4 border-gray-200"
                                    style={{ borderTopColor: config.color }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}